import Component from '@glimmer/component';
import { service } from '@ember/service';
import { fn, hash, concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { and, eq, not } from 'ember-truth-helpers';
import {
  HdsTabs,
  HdsTable,
  HdsButton,
  HdsLinkStandalone,
} from '@hashicorp/design-system-components/components';
import {
  Typography,
  DisclosureGuide,
  ClipboardCopyButton,
} from 'core/components';
import { boxPadding } from 'core/modifiers';
import Accordion from 'networks-common/components/networks/accordion';
import List from 'hcp/components/page/hvn-routes/list';

/**
 *
 * `PageHvnPeeringsConfigurationAccordionWebConsole` description here.
 *
 *
 * ```
 * <Page::HvnPeerings::ConfigurationAccordion::WebConsole
 *   @model={{@model}}
 *   @connectionMethod={{@connectionMethod}}
 *   @connection={{hash region=this.region cidrBlock=this.cidrBlock}}
 *   @products={{this.products}}
 *   @product={{@product}}
 *   @awsDisclosureGuides={{this.awsDisclosureGuides}}
 * />
 * ```
 *
 * @class PageHvnPeeringsConfigurationAccordionWebConsole
 *
 */
export default class PageHvnPeeringsConfigurationAccordionWebConsole extends Component {
  @service router;

  updateQueryParams = (queryParams = {}) => {
    return this.router.transitionTo(this.router.currentRouteName, {
      queryParams,
    });
  };

  <template>
    <div
      class="page-hvn-peerings-configuration-accordion-web-console"
      {{boxPadding "xl 0 0 0"}}
    >
      <Accordion
        id="configure-web-console-accordion-one"
        @label={{t
          "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-one.title"
        }}
        @isInitiallyOpen={{unless @completion.securityGroupMarkedComplete true}}
        @displayBadge={{if
          (and @completion.securityGroupMarkedComplete @displayBadge)
          true
        }}
        @isSuccess={{true}}
      >
        <div {{boxPadding "0 0 xl 0"}}>
          <Typography
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            {{boxPadding "0 0 md 0"}}
          >
            {{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-one.description"
              hvnID=@model.network.id
              htmlSafe=true
            }}
          </Typography>
          <DisclosureGuide
            @content={{@awsDisclosureGuides.updatingSecurityGroups}}
          />
        </div>

        <HdsTabs
          data-test-peering-web-console-rules-tabs-aws
          {{boxPadding "0 0 xl 0"}}
          as |T|
        >
          <T.Tab
            @icon="vault"
            @isSelected={{eq @product @products.vault.id}}
            data-test-peering-web-console-vault-tab-aws
            {{on
              "click"
              (fn this.updateQueryParams (hash product=@products.vault.id))
            }}
          >
            {{t "components.networks.content-tabs.vault-rules.label"}}
          </T.Tab>
          <T.Panel />

          <T.Tab
            @icon="consul-color"
            @isSelected={{eq @product @products.consul.id}}
            data-test-peering-web-console-consul-tab-aws
            {{on
              "click"
              (fn this.updateQueryParams (hash product=@products.consul.id))
            }}
          >
            {{t "components.networks.content-tabs.consul-rules.label"}}
          </T.Tab>
          <T.Panel />
        </HdsTabs>

        {{#if (eq @product @products.consul.id)}}
          <div
            class="hvnsPeeringsDetail__instructions--inboundRules"
            {{boxPadding "0 0 xl 0"}}
          >
            <Typography
              @variant="h3"
              class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-strong"
              {{boxPadding "0 0 md 0"}}
            >
              {{t
                "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.inbound-rules"
              }}
            </Typography>
            {{! INBOUND TABLE }}
            {{! TODO HCPE-1639: Replace tables }}
            <HdsTable
              data-test-peering-web-console-inbound-rules-consul-aws
              @valign="middle"
              @caption={{t
                "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.table.caption"
              }}
            >
              <:head as |H|>
                <H.Tr>
                  <H.Th>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.protocol"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.port-range"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.source"
                    }}
                  </H.Th>
                  <H.Th>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.description"
                    }}
                  </H.Th>
                </H.Tr>
              </:head>
              <:body as |B|>
                <B.Tr data-test-list-ingress-item>
                  <B.Td>
                    <b>
                      {{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                      }}
                    </b>
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @clipboardText={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                  <B.Td>
                    {{@connection.cidrBlock}}
                    <ClipboardCopyButton
                      title={{@connection.cidrBlock}}
                      @clipboardText={{@connection.cidrBlock}}
                      @variant="no-success-text"
                    />
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                      }}
                      @clipboardText={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                </B.Tr>
                <B.Tr data-test-list-ingress-item>
                  <B.Td>
                    <b>
                      {{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.udp"
                      }}
                    </b>
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @clipboardText={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                  <B.Td>
                    {{@connection.cidrBlock}}
                    <ClipboardCopyButton
                      title={{@connection.cidrBlock}}
                      @clipboardText={{@connection.cidrBlock}}
                      @variant="no-success-text"
                    />
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                      }}
                      @clipboardText={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-from-server"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                </B.Tr>
                <B.Tr data-test-list-ingress-item>
                  <B.Td>
                    <b>
                      {{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                      }}
                    </b>
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @clipboardText={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.page.hvn-peerings.detail.peering-details.labels.security-group-id"
                    }}
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                      }}
                      @clipboardText={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                </B.Tr>
                <B.Tr data-test-list-ingress-item>
                  <B.Td>
                    <b>
                      {{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.udp"
                      }}
                    </b>
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @clipboardText={{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.page.hvn-peerings.detail.peering-details.labels.security-group-id"
                    }}
                  </B.Td>
                  <B.Td>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                    }}
                    <ClipboardCopyButton
                      title={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                      }}
                      @clipboardText={{t
                        "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.handle-gossip-between-agents"
                      }}
                      @variant="no-success-text"
                    />
                  </B.Td>
                </B.Tr>
              </:body>
            </HdsTable>
          </div>
        {{/if}}
        <div
          class="hvnsPeeringsDetail__instructions--outboundRules"
          {{boxPadding "0 0 lg 0"}}
        >
          <Typography
            @variant="h3"
            class="hds-typography-body-200 hds-font-weight-semibold hds-foreground-strong"
            {{boxPadding "0 0 md 0"}}
          >
            {{t
              "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.outbound-rules"
            }}
          </Typography>
          {{! OUTBOUND TABLE }}
          {{! TODO HCPE-1639: Replace tables }}
          <HdsTable
            data-test-peering-web-console-outbound-rules-consul-aws
            @valign="middle"
            @caption={{t
              "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.table.caption"
            }}
          >
            <:head as |H|>
              <H.Tr>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.protocol"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.port-range"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.destination"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.description"
                  }}
                </H.Th>
              </H.Tr>
            </:head>
            <:body as |B|>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8300"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8300"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8300"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.clients-to-talk-to-server"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.clients-to-talk-to-server"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.clients-to-talk-to-server"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-with-server"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-with-server"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-with-server"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.udp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-with-server"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-with-server"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-with-server"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.peering-details.labels.security-group-id"
                  }}
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-between-agents"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-between-agents"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-between-agents"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.udp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8301"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.peering-details.labels.security-group-id"
                  }}
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-between-agents"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-between-agents"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.gossip-between-agents"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.80"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.80"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.80"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.http-api"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.http-api"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.http-api"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.443"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.443"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.443"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.https-api"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.https-api"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.https-api"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
            </:body>
          </HdsTable>
          <HdsTable
            data-test-peering-web-console-outbound-rules-vault-aws
            @valign="middle"
            @caption={{t
              "components.page.hvn-peerings.detail.instructions.default.updating-groups.web-console.table.caption"
            }}
          >
            <:head as |H|>
              <H.Tr>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.protocol"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.port-range"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.destination"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.headers.description"
                  }}
                </H.Th>
              </H.Tr>
            </:head>
            <:body as |B|>

              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8200"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8200"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.8200"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.https-api"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.https-api"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.https-api"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
              <B.Tr data-test-list-egress-item>
                <B.Td>
                  <b>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.tcp"
                    }}
                  </b>
                </B.Td>
                <B.Td>
                  {{t
                    "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.5696"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.5696"
                    }}
                    @clipboardText={{t
                      "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.ports.5696"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.kmip-secret-engine"
                  }}
                  <ClipboardCopyButton
                    title={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.kmip-secret-engine"
                    }}
                    @clipboardText={{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.updating-groups.web-console.table.labels.kmip-secret-engine"
                    }}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
            </:body>
          </HdsTable>
          {{#if @displayBadge}}
            <div {{boxPadding "lg 0 0"}}>
              {{#unless @completion.securityGroupMarkedComplete}}
                <div {{boxPadding "0 0 md"}}>
                  <Typography>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.mark-complete-security-group-help"
                    }}
                  </Typography>
                </div>
              {{/unless}}
              <div>
                <HdsButton
                  @text={{t
                    (concat
                      "components.page.hvn-peerings.detail.instructions.aws.default."
                      (unless
                        @completion.securityGroupMarkedComplete
                        "mark-complete"
                        "mark-incomplete"
                      )
                    )
                  }}
                  @color="secondary"
                  @size="small"
                  {{on "click" @markSecurityGroupAsComplete}}
                />
              </div>
            </div>
          {{/if}}
        </div>
      </Accordion>
      <Accordion
        id="configure-web-console-accordion-two"
        @label={{t
          "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-two.title"
        }}
        @isInitiallyOpen={{if
          (and
            @completion.securityGroupMarkedComplete
            (not @completion.routeTableMarkedComplete)
          )
          true
        }}
        @displayBadge={{if
          (and @completion.routeTableMarkedComplete @displayBadge)
          true
        }}
        @isSuccess={{true}}
      >
        <div>
          <Typography
            class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            {{boxPadding "0 0 md 0"}}
          >
            {{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-two.description"
            }}
          </Typography>
          <div {{boxPadding "0 0 sm 0"}}>
            <HdsLinkStandalone
              @text={{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-two.learn-link"
              }}
              @icon="learn-link"
              @iconPosition="trailing"
              @href={{t
                "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-two.learn-link-url"
              }}
            />
          </div>
          <div {{boxPadding "xs 0 xl 0"}}>
            <DisclosureGuide
              @content={{@awsDisclosureGuides.updatingRouteTable}}
            />
          </div>

          {{! ROUTE TABLE }}
          {{! TODO HCPE-1639: Replace tables }}
          <HdsTable
            @caption={{t
              "components.page.hvn-peerings.detail.instructions.aws.default.updating-route-table.web-console.table.caption"
            }}
          >
            <:head as |H|>
              <H.Tr>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-route-table.web-console.table.headers.destination"
                  }}
                </H.Th>
                <H.Th>
                  {{t
                    "components.page.hvn-peerings.detail.instructions.aws.default.updating-route-table.web-console.table.headers.target"
                  }}
                </H.Th>
              </H.Tr>
            </:head>
            <:body as |B|>
              <B.Tr data-test-list-item>
                <B.Td>
                  {{@connection.cidrBlock}}
                  <ClipboardCopyButton
                    title={{@connection.cidrBlock}}
                    @clipboardText={{@connection.cidrBlock}}
                    @variant="no-success-text"
                  />
                </B.Td>
                <B.Td>
                  {{@connection.providerPeeringId}}
                  <ClipboardCopyButton
                    title={{@connection.providerPeeringId}}
                    @clipboardText={{@connection.providerPeeringId}}
                    @variant="no-success-text"
                  />
                </B.Td>
              </B.Tr>
            </:body>
          </HdsTable>
          {{#if @displayBadge}}
            <div {{boxPadding "lg 0 0"}}>
              {{#unless @completion.routeTableMarkedComplete}}
                <div {{boxPadding "0 0 md"}}>
                  <Typography>
                    {{t
                      "components.page.hvn-peerings.detail.instructions.aws.default.mark-complete-route-table-help"
                    }}
                  </Typography>
                </div>
              {{/unless}}
              <div>
                <HdsButton
                  @text={{t
                    (concat
                      "components.page.hvn-peerings.detail.instructions.aws.default."
                      (unless
                        @completion.routeTableMarkedComplete
                        "mark-complete"
                        "mark-incomplete"
                      )
                    )
                  }}
                  @color="secondary"
                  @size="small"
                  {{on "click" @markRouteTableAsComplete}}
                />
              </div>
            </div>
          {{/if}}
        </div>
      </Accordion>
      <Accordion
        id="configure-web-console-accordion-three"
        @label={{t
          "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-three.title"
        }}
        @displayBadge={{true}}
        @isOptional={{true}}
      >
        <div>
          <Typography class="hds-typography-body-200 hds-foreground-primary">
            {{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-three.help-text"
              hvnID=@model.network.id
              htmlSafe=true
            }}
          </Typography>
          <HdsLinkStandalone
            @text={{t
              "components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-three.learn-link"
            }}
            @icon="learn-link"
            @iconPosition="trailing"
            @href="components.networks.peerings.aws.manual.guided-flow.manual.detail.configure.web-console.accordion-three.learn-link-url"
            {{boxPadding "sm 0 0"}}
          />
        </div>
        <List @isHvnHvnPeering={{this.isHvnHvnPeering}} @model={{@model}} />
      </Accordion>
    </div>
  </template>
}
